import { isEmpty } from 'lodash';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI, Selector } from 'sonata-react-components';
import { DeviceCarrierApiResponse } from 'sonata-static-storage/lib/common/common.types';
import { worldCountries } from 'sonata-static-storage/lib/samples/worldCountries.samples';

import { CarriersSelectionStyled } from './CarriersSelection.styled';

import type { CarrierEntryType, CarriersType } from 'sonata-static-storage/lib/schemas/targeting/Carrier/Carrier.types';

type Props = {
  availableCarriers?: Record<string, DeviceCarrierApiResponse>;
  campaignCountry: string;
  carriers: CarriersType;
  countriesForSelector?: string[];
  id: string;
  onChangeCarriers: (selectedCarriers: CarriersType) => void;
};

type State = {
  otherCountryCarriers: CarriersType;
  selectedCountry?: string;
  selectedCountryCarriers: CarriersType;
};

function obtainOtherCountryCarriers(
  availableCarriers: Record<string, DeviceCarrierApiResponse>,
  carriers: CarriersType,
  country?: string
): CarriersType {
  if (!country) {
    return [];
  }
  return carriers.reduce((totalAcc: CarriersType, carrier) => {
    return totalAcc.concat(
      Object.entries(carrier).reduce((acc: CarrierEntryType, [carrierKey]) => {
        const { brand, countryCode } = availableCarriers[carrierKey];
        if (countryCode.toLowerCase() !== country.toLowerCase()) {
          return {
            ...acc,
            [carrierKey]: brand
          };
        }
        return acc;
      }, {})
    );
  }, []);
}

export const CarriersSelection: React.FC<Props> = ({
  availableCarriers = {},
  campaignCountry,
  carriers,
  countriesForSelector,
  id,
  onChangeCarriers
}) => {
  const { formatMessage } = useIntl();

  const [{ otherCountryCarriers, selectedCountry, selectedCountryCarriers }, setState] = useState<State>({
    otherCountryCarriers: obtainOtherCountryCarriers(availableCarriers, carriers, campaignCountry),
    selectedCountry: campaignCountry.toLowerCase(),
    selectedCountryCarriers: []
  });

  const countryOptions = [
    ...new Set(Object.entries(availableCarriers).map(([, { countryCode }]) => countryCode.toLowerCase()))
  ]
    .filter(countryCode => {
      return countriesForSelector?.length ? countriesForSelector.includes(countryCode) : true;
    })
    .map(countryCode => ({
      id: countryCode,
      label: worldCountries.find(({ cca3 }) => countryCode.toLowerCase() === cca3.toLowerCase())?.name?.common || 'N/A'
    }))
    .sort(({ label: label1 }, { label: label2 }) => label1.localeCompare(label2));

  const carriersOptionsForSelectedCountry = selectedCountry
    ? Object.entries(availableCarriers)
        .filter(([, { countryCode }]) => {
          return countryCode.toLowerCase() === selectedCountry.toLowerCase();
        })
        .map(([carrierKey, { brand }]) => ({
          id: carrierKey,
          label: brand
        }))
    : [];
  carriersOptionsForSelectedCountry.sort(({ label: label1 }, { label: label2 }) => {
    return label1.localeCompare(label2, undefined, { sensitivity: 'base' });
  });

  const carriersForSelectedCountry = selectedCountry
    ? Object.entries(availableCarriers)
        .filter(([, { countryCode }]) => {
          return countryCode.toLowerCase() === selectedCountry.toLowerCase();
        })
        .filter(([carrierKey]) => carriers.some(cK => Object.keys(cK).includes(carrierKey)))
        .map(([carrierKey, { brand }]) => ({
          id: carrierKey,
          label: brand
        }))
    : [];

  const handleCountrySelectorChanged = ({ id: countryCode }: { id: string }) => {
    setState(prevState => ({
      ...prevState,
      otherCountryCarriers: obtainOtherCountryCarriers(availableCarriers, carriers, countryCode.toLowerCase()),
      selectedCountry: countryCode.toLowerCase(),
      selectedCountryCarriers: []
    }));
  };

  const handleCarriersSelectorChanged = (selectedCarriers: { id: string; label: string }[]) => {
    setState(prevState => ({
      ...prevState,
      selectedCountryCarriers: selectedCarriers.map(({ id: carrierId, label: carrierLabel }) => ({
        [carrierId]: carrierLabel
      }))
    }));
  };

  const handleAddCarrierClick = () => {
    onChangeCarriers(otherCountryCarriers.concat(selectedCountryCarriers));
  };

  return (
    <CarriersSelectionStyled className="carriersSelectionContainer">
      <div className="carriersSelectionContainer__selectors">
        <Selector
          className="carriersSelectionContainer__selectors__countrySelector"
          defaultValue={countryOptions.filter(({ id: countryCode }) => {
            return countryCode.toLowerCase() === campaignCountry.toLowerCase();
          })}
          id={`${id}_country_selector`}
          onChange={handleCountrySelectorChanged}
          options={countryOptions}
          tiny
          type="simple"
        />
        <Selector
          className="carriersSelectionContainer__selectors__carriersSelector"
          defaultValue={carriersForSelectedCountry}
          id={`${id}_carrier_selector`}
          isDisabled={!selectedCountry}
          key={`${id}_carrier_selector_${selectedCountry || 'N/A'}_${carriersForSelectedCountry.length}`}
          onChange={handleCarriersSelectorChanged}
          options={carriersOptionsForSelectedCountry}
          tiny
          type="multiple"
        />
      </div>
      <ButtonUI
        className="carriersSelectionContainer__addButton"
        disabled={isEmpty(selectedCountryCarriers)}
        icon="fas fa-plus"
        id={`${id}_add_carrier_button`}
        onClick={handleAddCarrierClick}
        size="ultraSmall"
        text={formatMessage({
          defaultMessage: 'Add',
          id: 'common.add'
        })}
      />
    </CarriersSelectionStyled>
  );
};
